import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Alert from 'react-bootstrap/Alert';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import { api } from '../../lib/api';
import FacilitatorPageHeader from './FacilitatorPageHeader';

const MODULE_LABELS = {
  case: 'Case study',
  drill: 'Calculation',
  'spot-error': 'Spot-the-error',
  outpatient: 'Outpatient',
  cpd: 'CPD',
};

const MODULE_BADGE = {
  case: 'primary',
  drill: 'info',
  'spot-error': 'warning',
  outpatient: 'dark',
  cpd: 'light',
};

const CPD_TYPE_LABELS = {
  article: 'Article / reading',
  course: 'Course / training',
  audit: 'Audit',
  intervention: 'Clinical intervention',
  other: 'Other',
};

const formatDate = (value) => {
  if (!value) return '—';
  try {
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return new Date(`${value}T00:00:00`).toLocaleDateString('en-GB');
    }
    return new Date(value).toLocaleDateString('en-GB');
  } catch {
    return String(value);
  }
};

const formatRelative = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString('en-GB');
  } catch {
    return String(value);
  }
};

const FacilitatorStudentPortfolios = ({ token, user, onBack }) => {
  // 'triage' lands the lecturer on the cross-student queue of entries
  // that nobody has commented on yet. 'browse' is the per-student
  // dashboard for searching out specific students' portfolios.
  const [activeTab, setActiveTab] = useState('triage');

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const [selectedStudentId, setSelectedStudentId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');

  const [selectedPortfolioId, setSelectedPortfolioId] = useState('');

  // Per-entry lecturer comment editor state. Keyed by `${entryKind}:${entryId}`
  // so each row in the accordion has its own draft + feedback.
  const [commentDrafts, setCommentDrafts] = useState({});
  const [savingCommentKey, setSavingCommentKey] = useState(null);
  const [commentFeedback, setCommentFeedback] = useState({});
  const currentLecturerId = user?.id || '';

  // Triage feed state (cross-student unreviewed entries).
  const [triage, setTriage] = useState({ entries: [], total: 0, truncated: false });
  const [triageLoading, setTriageLoading] = useState(false);
  const [triageError, setTriageError] = useState('');
  const [triageDrafts, setTriageDrafts] = useState({});
  const [triageSavingKey, setTriageSavingKey] = useState(null);
  const [triageFeedback, setTriageFeedback] = useState({});

  const loadStudents = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const result = await api.listStudentsWithPortfolios(token);
      setStudents(Array.isArray(result.students) ? result.students : []);
      setError('');
    } catch (err) {
      setError(err?.message || 'Failed to load students.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadStudentDetail = useCallback(async (studentId) => {
    if (!token || !studentId) return;
    try {
      setDetailLoading(true);
      setDetailError('');
      const result = await api.getStudentPortfolioDetail(studentId, token);
      setDetail(result);
      setSelectedPortfolioId('');
    } catch (err) {
      setDetailError(err?.message || 'Failed to load student detail.');
    } finally {
      setDetailLoading(false);
    }
  }, [token]);

  const loadTriage = useCallback(async () => {
    if (!token) return;
    try {
      setTriageLoading(true);
      setTriageError('');
      const result = await api.listPortfolioTriage(token);
      setTriage({
        entries: Array.isArray(result?.entries) ? result.entries : [],
        total: Number(result?.total) || 0,
        truncated: Boolean(result?.truncated),
      });
    } catch (err) {
      setTriageError(err?.message || 'Failed to load triage queue.');
    } finally {
      setTriageLoading(false);
    }
  }, [token]);

  useEffect(() => { loadStudents(); }, [loadStudents]);
  useEffect(() => { loadTriage(); }, [loadTriage]);

  useEffect(() => {
    if (selectedStudentId) loadStudentDetail(selectedStudentId);
    else setDetail(null);
  }, [selectedStudentId, loadStudentDetail]);

  // On phones the student list and the detail pane stack vertically.
  // Tapping a student looks like a no-op until the lecturer scrolls
  // down past the list. Scroll the detail pane into view when the
  // selection changes (lg+ users see the side-by-side layout already).
  const detailPaneRef = useRef(null);
  useEffect(() => {
    if (!selectedStudentId) return;
    if (typeof window === 'undefined') return;
    if (window.innerWidth >= 992) return; // Bootstrap lg breakpoint
    const el = detailPaneRef.current;
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedStudentId]);

  const filteredStudents = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return students;
    return students.filter((s) => (
      (s.displayName || '').toLowerCase().includes(q)
      || (s.email || '').toLowerCase().includes(q)
    ));
  }, [students, search]);

  // Only the entries inside the selected portfolio are shown. The lecturer
  // is reviewing curated work, not auditing the student's full activity feed.
  const visibleEntries = useMemo(() => {
    if (!detail || !selectedPortfolioId) return [];
    return detail.entries.filter((e) => Array.isArray(e.portfolioIds) && e.portfolioIds.includes(selectedPortfolioId));
  }, [detail, selectedPortfolioId]);

  const currentPortfolio = useMemo(() => {
    if (!detail || !selectedPortfolioId) return null;
    return detail.portfolios.find((p) => p.id === selectedPortfolioId) || null;
  }, [detail, selectedPortfolioId]);

  const handleCommentChange = (entry, value) => {
    const key = `${entry.entryKind}:${entry.entryId}`;
    setCommentDrafts((prev) => ({ ...prev, [key]: value }));
    setCommentFeedback((prev) => ({ ...prev, [key]: '' }));
  };

  const handleCommentSave = async (entry) => {
    if (!detail) return;
    const key = `${entry.entryKind}:${entry.entryId}`;
    const existing = (entry.lecturerComments || []).find((c) => c.lecturerUserId === currentLecturerId);
    const draftBody = (commentDrafts[key] ?? existing?.body ?? '').trim();
    if (!draftBody) {
      setCommentFeedback((prev) => ({ ...prev, [key]: 'Comment cannot be empty.' }));
      return;
    }
    setSavingCommentKey(key);
    try {
      const result = await api.upsertLecturerComment(
        {
          studentUserId: detail.student.id,
          entryKind: entry.entryKind,
          entryId: entry.entryId,
          body: draftBody,
        },
        token
      );
      // Merge the saved comment back into our local detail so the UI updates
      // without a full refetch.
      setDetail((prev) => {
        if (!prev) return prev;
        const updated = prev.entries.map((row) => {
          if (`${row.entryKind}:${row.entryId}` !== key) return row;
          const others = (row.lecturerComments || []).filter((c) => c.lecturerUserId !== currentLecturerId);
          return {
            ...row,
            lecturerComments: [
              { ...result.comment, lecturerUserId: currentLecturerId },
              ...others,
            ],
          };
        });
        return { ...prev, entries: updated };
      });
      setCommentDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setCommentFeedback((prev) => ({ ...prev, [key]: 'Saved' }));
    } catch (err) {
      setCommentFeedback((prev) => ({
        ...prev,
        [key]: err?.payload?.error || err?.message || 'Save failed',
      }));
    } finally {
      setSavingCommentKey(null);
    }
  };

  const handleCommentDelete = async (entry) => {
    if (!detail) return;
    const key = `${entry.entryKind}:${entry.entryId}`;
    setSavingCommentKey(key);
    try {
      await api.deleteLecturerComment(
        { entryKind: entry.entryKind, entryId: entry.entryId },
        token
      );
      setDetail((prev) => {
        if (!prev) return prev;
        const updated = prev.entries.map((row) => {
          if (`${row.entryKind}:${row.entryId}` !== key) return row;
          const remaining = (row.lecturerComments || []).filter((c) => c.lecturerUserId !== currentLecturerId);
          return { ...row, lecturerComments: remaining };
        });
        return { ...prev, entries: updated };
      });
      setCommentDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setCommentFeedback((prev) => ({ ...prev, [key]: 'Cleared' }));
    } catch (err) {
      setCommentFeedback((prev) => ({
        ...prev,
        [key]: err?.payload?.error || err?.message || 'Delete failed',
      }));
    } finally {
      setSavingCommentKey(null);
    }
  };

  const stats = useMemo(() => {
    let cpdHours = 0;
    let reflected = 0;
    let tagged = 0;
    for (const e of visibleEntries) {
      if (e.kind === 'cpd') cpdHours += Number(e.hours) || 0;
      if (e.reflection?.body && e.reflection.body.trim()) reflected += 1;
      if (Array.isArray(e.tags) && e.tags.length) tagged += 1;
    }
    return { cpdHours, reflected, tagged, total: visibleEntries.length };
  }, [visibleEntries]);

  const renderRowSummary = (e) => {
    const moduleKind = e.entryKind || e.moduleType;
    const dateLabel = e.kind === 'cpd' ? 'Logged' : 'Completed';
    return (
      <div className="d-flex flex-column w-100 gap-1 me-2">
        <div className="d-flex flex-wrap align-items-center gap-2">
          <Badge bg={MODULE_BADGE[moduleKind] || 'secondary'} text={moduleKind === 'cpd' ? 'dark' : undefined}>
            {MODULE_LABELS[moduleKind] || moduleKind}
          </Badge>
          <span className="fw-semibold" style={{ fontSize: '1.05rem' }}>
            {e.sourceTitle || '(untitled)'}
          </span>
        </div>
        <div className="d-flex flex-wrap align-items-center gap-2 small text-muted">
          <span><strong>{dateLabel}:</strong> {formatDate(e.occurredAt)}</span>
          {e.kind === 'cpd' && e.entryType ? (
            <>
              <span>·</span>
              <span><strong>Type:</strong> {CPD_TYPE_LABELS[e.entryType] || e.entryType}</span>
            </>
          ) : null}
          {e.kind === 'cpd' && e.hours != null ? (
            <>
              <span>·</span>
              <span><strong>Hours:</strong> {Number(e.hours).toFixed(Number.isInteger(Number(e.hours)) ? 0 : 1)}h</span>
            </>
          ) : null}
          {e.kind === 'session' && e.effectiveScore != null ? (
            <>
              <span>·</span>
              <span>
                <strong>Score:</strong> {Math.round(e.effectiveScore)}%
                {e.scoreOverride != null ? ' (adjusted)' : ''}
              </span>
            </>
          ) : null}
          {/* Cap to two chips - drop tag-count from the row (visible
              inside the expanded body alongside the tag chips themselves). */}
          <div className="ms-auto d-flex flex-wrap gap-1">
            {Array.isArray(e.lecturerComments) && e.lecturerComments.length > 0 ? (
              <Badge bg="info" pill className="border" title="Lecturer feedback recorded">
                <i className="bi bi-chat-left-text me-1" />Feedback
              </Badge>
            ) : null}
            {e.reflection?.body ? (
              <Badge bg="success" pill className="border" title="Student has reflected">
                <i className="bi bi-pencil-square me-1" />Reflected
              </Badge>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderEntryBody = (e) => {
    const tags = Array.isArray(e.tags) ? e.tags : [];
    const key = `${e.entryKind}:${e.entryId}`;
    const allComments = Array.isArray(e.lecturerComments) ? e.lecturerComments : [];
    const myComment = allComments.find((c) => c.lecturerUserId === currentLecturerId);
    const otherComments = allComments.filter((c) => c.lecturerUserId !== currentLecturerId);
    const draftValue = Object.prototype.hasOwnProperty.call(commentDrafts, key)
      ? commentDrafts[key]
      : (myComment?.body || '');
    const baseValue = myComment?.body || '';
    const dirty = draftValue !== baseValue;
    const feedback = commentFeedback[key] || '';
    return (
      <div>
        {tags.length > 0 ? (
          <div className="d-flex flex-wrap gap-1 mb-3">
            {tags.map((t) => (
              <Badge key={t.outcomeItemId} bg="info" className="border" title={t.label}>
                {t.code} — {t.label}
              </Badge>
            ))}
          </div>
        ) : null}
        {e.kind === 'cpd' && e.cpdReflection ? (
          <div className="small text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
            <strong>CPD notes:</strong> {e.cpdReflection}
          </div>
        ) : null}
        {e.kind === 'session' && e.reviewComment ? (
          <div className="small text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
            <strong>Auto-grading review:</strong> {e.reviewComment}
          </div>
        ) : null}
        {e.reflection?.body ? (
          <div className="mb-3">
            <div className="small text-muted mb-1">
              <strong>Student reflection</strong>
              {e.reflection.updatedAt ? (
                <span className="ms-2">last saved {formatRelative(e.reflection.updatedAt)}</span>
              ) : null}
            </div>
            <div className="p-3 rounded" style={{ background: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
              {e.reflection.body}
            </div>
          </div>
        ) : (
          <div className="small text-muted fst-italic mb-3">No reflection recorded.</div>
        )}

        {otherComments.length > 0 ? (
          <div className="mb-3">
            <div className="small text-muted mb-1">
              <strong>Comments from other lecturers</strong>
            </div>
            {otherComments.map((c) => (
              <div
                key={c.id}
                className="p-3 rounded mb-2"
                style={{ background: '#eef6ff', whiteSpace: 'pre-wrap' }}
              >
                <div className="small text-muted mb-1">
                  <strong>{c.lecturerName || 'Lecturer'}</strong>
                  <span className="ms-2">{formatRelative(c.updatedAt)}</span>
                </div>
                {c.body}
              </div>
            ))}
          </div>
        ) : null}

        <div className="border-top pt-3">
          <Form.Label className="small text-muted mb-1">
            <strong>Your comment</strong>
            {myComment?.updatedAt ? (
              <span className="ms-2">last saved {formatRelative(myComment.updatedAt)}</span>
            ) : null}
          </Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            placeholder="Leave a comment for this entry. The student will see it in their portfolio."
            value={draftValue}
            onChange={(ev) => handleCommentChange(e, ev.target.value)}
          />
          <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
            <span className="small text-muted">{feedback}</span>
            <div className="d-flex gap-2">
              {myComment ? (
                <Button
                  size="sm"
                  variant="outline-danger"
                  onClick={() => handleCommentDelete(e)}
                  disabled={savingCommentKey === key}
                >
                  <i className="bi bi-trash me-1" /> Delete
                </Button>
              ) : null}
              <Button
                size="sm"
                variant="primary"
                onClick={() => handleCommentSave(e)}
                disabled={!dirty || savingCommentKey === key}
              >
                {savingCommentKey === key ? 'Saving…' : (myComment ? 'Update' : 'Post comment')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handleTriageDraftChange = (entry, value) => {
    const key = `${entry.entryKind}:${entry.entryId}`;
    setTriageDrafts((prev) => ({ ...prev, [key]: value }));
    setTriageFeedback((prev) => ({ ...prev, [key]: '' }));
  };

  const handleTriageSave = async (entry) => {
    const key = `${entry.entryKind}:${entry.entryId}`;
    const body = (triageDrafts[key] || '').trim();
    if (!body) {
      setTriageFeedback((prev) => ({ ...prev, [key]: 'Comment cannot be empty.' }));
      return;
    }
    setTriageSavingKey(key);
    try {
      await api.upsertLecturerComment(
        {
          studentUserId: entry.student.id,
          entryKind: entry.entryKind,
          entryId: entry.entryId,
          body,
        },
        token
      );
      // Once a comment exists, the entry no longer qualifies as
      // "needs review" - drop it from the local queue.
      setTriage((prev) => ({
        ...prev,
        entries: prev.entries.filter((e) => `${e.entryKind}:${e.entryId}` !== key),
        total: Math.max(0, prev.total - 1),
      }));
      setTriageDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      setTriageFeedback((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      // Refresh the per-student detail if the lecturer happens to be
      // viewing that student on the Browse tab so the chip updates.
      if (selectedStudentId === entry.student.id) {
        loadStudentDetail(entry.student.id);
      }
    } catch (err) {
      setTriageFeedback((prev) => ({
        ...prev,
        [key]: err?.payload?.error || err?.message || 'Save failed',
      }));
    } finally {
      setTriageSavingKey(null);
    }
  };

  const renderTriagePane = () => {
    if (triageLoading && triage.entries.length === 0) {
      return <div className="text-center my-5"><Spinner animation="border" /></div>;
    }
    if (triageError) {
      return <Alert variant="danger">{triageError}</Alert>;
    }
    if (triage.entries.length === 0) {
      return (
        <Alert variant="success" className="d-flex align-items-center gap-2">
          <i className="bi bi-check2-circle" />
          <span>Inbox zero — every portfolio entry has at least one lecturer comment.</span>
        </Alert>
      );
    }
    return (
      <>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2 small text-muted">
          <span>
            {triage.total} entr{triage.total === 1 ? 'y' : 'ies'} need
            {triage.total === 1 ? 's' : ''} review
            {triage.truncated ? ' (showing the most recent 200)' : ''}
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={loadTriage}
            disabled={triageLoading}
          >
            <i className="bi bi-arrow-clockwise me-1" />
            {triageLoading ? 'Refreshing…' : 'Refresh'}
          </Button>
        </div>
        <Accordion>
          {triage.entries.map((e) => {
            const key = `${e.entryKind}:${e.entryId}`;
            const draft = triageDrafts[key] ?? '';
            const feedback = triageFeedback[key] || '';
            const tags = Array.isArray(e.tags) ? e.tags : [];
            const portfolios = Array.isArray(e.portfolios) ? e.portfolios : [];
            return (
              <Accordion.Item eventKey={key} key={key}>
                <Accordion.Header>
                  <div className="d-flex flex-column w-100 gap-1 me-2">
                    <div className="d-flex flex-wrap align-items-center gap-2">
                      <Badge bg={MODULE_BADGE[e.entryKind] || 'secondary'} text={e.entryKind === 'cpd' ? 'dark' : undefined}>
                        {MODULE_LABELS[e.entryKind] || e.entryKind}
                      </Badge>
                      <span className="fw-semibold" style={{ fontSize: '1.05rem' }}>
                        {e.sourceTitle || '(untitled)'}
                      </span>
                    </div>
                    <div className="d-flex flex-wrap align-items-center gap-2 small text-muted">
                      <span><i className="bi bi-person me-1" />{e.student.displayName || e.student.email}</span>
                      <span>·</span>
                      <span>{formatDate(e.occurredAt)}</span>
                      {portfolios.length > 0 ? (
                        <>
                          <span>·</span>
                          <span>
                            <i className="bi bi-journal-bookmark me-1" />
                            {portfolios.map((p) => p.name).join(', ')}
                          </span>
                        </>
                      ) : null}
                      {e.reflection?.body ? (
                        <Badge bg="success" pill className="border ms-auto">
                          <i className="bi bi-pencil-square me-1" />Reflected
                        </Badge>
                      ) : null}
                    </div>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  {tags.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1 mb-3">
                      {tags.map((t) => (
                        <Badge key={t.outcomeItemId} bg="info" className="border" title={t.label}>
                          {t.code} — {t.label}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                  {e.kind === 'cpd' && e.cpdReflection ? (
                    <div className="small text-muted mb-3" style={{ whiteSpace: 'pre-wrap' }}>
                      <strong>CPD notes:</strong> {e.cpdReflection}
                    </div>
                  ) : null}
                  {e.reflection?.body ? (
                    <div className="mb-3">
                      <div className="small text-muted mb-1">
                        <strong>Student reflection</strong>
                        {e.reflection.updatedAt ? (
                          <span className="ms-2">last saved {formatRelative(e.reflection.updatedAt)}</span>
                        ) : null}
                      </div>
                      <div className="p-3 rounded" style={{ background: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
                        {e.reflection.body}
                      </div>
                    </div>
                  ) : (
                    <div className="small text-muted fst-italic mb-3">No reflection recorded.</div>
                  )}
                  <div className="border-top pt-3">
                    <Form.Label className="small text-muted mb-1">
                      <strong>Your comment</strong>
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Leave a comment for this entry. The student will see it in their portfolio."
                      value={draft}
                      onChange={(ev) => handleTriageDraftChange(e, ev.target.value)}
                    />
                    <div className="d-flex justify-content-between align-items-center mt-2 flex-wrap gap-2">
                      <span className="small text-muted">{feedback}</span>
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => handleTriageSave(e)}
                        disabled={!draft.trim() || triageSavingKey === key}
                      >
                        {triageSavingKey === key ? 'Saving…' : 'Post comment'}
                      </Button>
                    </div>
                  </div>
                </Accordion.Body>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </>
    );
  };

  return (
    <div className="student-page">
      <Container className="mt-4 mb-5" fluid>
        <FacilitatorPageHeader
          title="Student portfolios"
          description="Review reflections and post feedback on students' portfolio entries."
          onBack={onBack}
        />

        <ButtonGroup className="mb-3 flex-wrap">
          <Button
            variant={activeTab === 'triage' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('triage')}
          >
            <i className="bi bi-inbox me-1" />
            Needs review
            {triage.total > 0 ? (
              <Badge bg="light" text="dark" className="ms-2">{triage.total}</Badge>
            ) : null}
          </Button>
          <Button
            variant={activeTab === 'browse' ? 'primary' : 'outline-primary'}
            onClick={() => setActiveTab('browse')}
          >
            <i className="bi bi-people me-1" />
            Browse students
          </Button>
        </ButtonGroup>

        {activeTab === 'triage' ? renderTriagePane() : null}

        {activeTab === 'browse' ? (
          <>
            {error ? <Alert variant="danger">{error}</Alert> : null}

            <Row className="g-3">
          <Col lg={4}>
            <Card className="shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Students</h6>
                  <span className="small text-muted">{filteredStudents.length} active</span>
                </div>
                <Form.Control
                  size="sm"
                  placeholder="Search by name or email…"
                  value={search}
                  onChange={(ev) => setSearch(ev.target.value)}
                  className="mb-2"
                />
                {loading ? (
                  <div className="text-center my-3"><Spinner animation="border" size="sm" /></div>
                ) : filteredStudents.length === 0 ? (
                  <Alert variant="info" className="mb-0 small">
                    {students.length === 0
                      ? 'No students have any portfolio activity yet.'
                      : 'No students match the search.'}
                  </Alert>
                ) : (
                  <div className="list-group">
                    {filteredStudents.map((s) => {
                      const active = s.id === selectedStudentId;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          className={`list-group-item list-group-item-action ${active ? 'active' : ''}`}
                          onClick={() => setSelectedStudentId(s.id)}
                        >
                          <div className="fw-semibold">{s.displayName || s.email}</div>
                          {s.displayName && s.email ? (
                            <div className="small text-muted">{s.email}</div>
                          ) : null}
                          <div className={`small mt-1 ${active ? 'text-light' : 'text-muted'}`}>
                            {s.portfolioCount} portfolio{s.portfolioCount === 1 ? '' : 's'}
                            <span className="mx-1">·</span>
                            {s.cpdCount} CPD
                            <span className="mx-1">·</span>
                            {s.sessionCount} session{s.sessionCount === 1 ? '' : 's'}
                          </div>
                          {s.lastActiveAt ? (
                            <div className="small text-muted mt-1">
                              Last active {formatRelative(s.lastActiveAt)}
                            </div>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8} ref={detailPaneRef}>
            {!selectedStudentId ? (
              <Alert variant="info">Select a student on the left to view their portfolios.</Alert>
            ) : detailLoading ? (
              <div className="text-center my-5"><Spinner animation="border" /></div>
            ) : detailError ? (
              <Alert variant="danger">{detailError}</Alert>
            ) : detail ? (
              <>
                <Card className="shadow-sm mb-3">
                  <Card.Body className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                      <h5 className="mb-1">{detail.student.displayName}</h5>
                      <div className="small text-muted">{detail.student.email}</div>
                    </div>
                    {currentPortfolio ? (
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => setSelectedPortfolioId('')}
                      >
                        <i className="bi bi-arrow-left me-1" /> All portfolios
                      </Button>
                    ) : null}
                  </Card.Body>
                </Card>

                {!currentPortfolio && Array.isArray(detail.goals) && detail.goals.length > 0 ? (
                  <Card className="shadow-sm mb-3">
                    <Card.Body>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0">
                          <i className="bi bi-flag me-1" />
                          Learning goals
                        </h6>
                        <span className="small text-muted">
                          {detail.goals.filter((g) => g.status === 'achieved').length}
                          {' / '}
                          {detail.goals.length} achieved
                        </span>
                      </div>
                      <div className="d-flex flex-column gap-2">
                        {detail.goals.map((g) => {
                          const linkedEntries = (g.links || [])
                            .map((l) => detail.entries.find((e) => (
                              e.entryKind === l.entryKind && e.entryId === l.entryId
                            )))
                            .filter(Boolean);
                          const achieved = g.status === 'achieved';
                          const variant = achieved ? 'success' : g.status === 'not_started' ? 'secondary' : 'primary';
                          const label = achieved ? 'Achieved' : g.status === 'not_started' ? 'Not started' : 'In progress';
                          return (
                            <div key={g.id} className="border rounded p-2">
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <Badge bg={variant}>{label}</Badge>
                                <span className="fw-semibold" style={achieved ? { textDecoration: 'line-through', color: '#6c757d' } : undefined}>
                                  {g.title}
                                </span>
                                {g.targetDate ? (
                                  <span className="small text-muted">
                                    · target {formatDate(g.targetDate)}
                                  </span>
                                ) : null}
                              </div>
                              {g.description ? (
                                <div className="small mb-1" style={{ whiteSpace: 'pre-wrap' }}>{g.description}</div>
                              ) : null}
                              {linkedEntries.length > 0 ? (
                                <div className="small text-muted">
                                  <i className="bi bi-link-45deg me-1" />
                                  {linkedEntries.length} linked entr{linkedEntries.length === 1 ? 'y' : 'ies'}: {linkedEntries.map((e) => e.sourceTitle).join(', ')}
                                </div>
                              ) : (
                                <div className="small text-muted fst-italic">No evidence linked yet.</div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </Card.Body>
                  </Card>
                ) : null}

                {!currentPortfolio ? (
                  detail.portfolios.length === 0 ? (
                    <Alert variant="info">
                      This student hasn&apos;t created any portfolios yet. They need to curate at
                      least one portfolio before you can review their work here.
                    </Alert>
                  ) : (
                    <Row className="g-3">
                      {detail.portfolios.map((p) => {
                        const portfolioEntries = detail.entries.filter((e) => (
                          Array.isArray(e.portfolioIds) && e.portfolioIds.includes(p.id)
                        ));
                        const reflectedHere = portfolioEntries.filter((e) => (
                          e.reflection?.body && e.reflection.body.trim()
                        )).length;
                        const cpdHoursHere = portfolioEntries.reduce((sum, e) => (
                          e.kind === 'cpd' ? sum + (Number(e.hours) || 0) : sum
                        ), 0);
                        return (
                          <Col md={6} key={p.id}>
                            <Card
                              role="button"
                              tabIndex={0}
                              onClick={() => setSelectedPortfolioId(p.id)}
                              onKeyDown={(ev) => {
                                if (ev.key === 'Enter' || ev.key === ' ') {
                                  ev.preventDefault();
                                  setSelectedPortfolioId(p.id);
                                }
                              }}
                              className="shadow-sm h-100 portfolio-card"
                              style={{ cursor: 'pointer' }}
                            >
                              <Card.Body className="d-flex flex-column">
                                <div className="d-flex justify-content-between align-items-start gap-2">
                                  <h5 className="mb-1">
                                    <i className="bi bi-journal-bookmark me-2" />
                                    {p.name}
                                  </h5>
                                  <Badge bg="primary" pill>{p.entryCount}</Badge>
                                </div>
                                {p.description ? (
                                  <div className="small text-muted mb-2">{p.description}</div>
                                ) : null}
                                <div className="d-flex flex-wrap gap-2 mt-auto pt-2 small text-muted">
                                  <span>
                                    <i className="bi bi-pencil-square me-1" />
                                    {reflectedHere} reflected
                                  </span>
                                  {cpdHoursHere > 0 ? (
                                    <span>
                                      <i className="bi bi-clock-history me-1" />
                                      {cpdHoursHere.toFixed(1)}h CPD
                                    </span>
                                  ) : null}
                                  <span className="ms-auto">
                                    Updated {formatDate(p.updatedAt)}
                                  </span>
                                </div>
                              </Card.Body>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  )
                ) : (
                  <>
                    <Card className="shadow-sm mb-3">
                      <Card.Body>
                        <h5 className="mb-1">{currentPortfolio.name}</h5>
                        {currentPortfolio.description ? (
                          <div className="small text-muted mb-2">{currentPortfolio.description}</div>
                        ) : null}
                        <Row className="g-3 mt-1">
                          <Col xs={6} md={3}>
                            <div className="small text-muted">Entries</div>
                            <div className="h5 mb-0">{stats.total}</div>
                          </Col>
                          <Col xs={6} md={3}>
                            <div className="small text-muted">CPD hours</div>
                            <div className="h5 mb-0">{stats.cpdHours.toFixed(1)}h</div>
                          </Col>
                          <Col xs={6} md={3}>
                            <div className="small text-muted">Reflected</div>
                            <div className="h5 mb-0">{stats.reflected}</div>
                          </Col>
                          <Col xs={6} md={3}>
                            <div className="small text-muted">Tagged</div>
                            <div className="h5 mb-0">{stats.tagged}</div>
                          </Col>
                        </Row>
                      </Card.Body>
                    </Card>

                    {visibleEntries.length === 0 ? (
                      <Alert variant="info">
                        This portfolio is empty &mdash; the student hasn&apos;t added any entries yet.
                      </Alert>
                    ) : (
                      <Accordion>
                        {visibleEntries.map((e) => (
                          <Accordion.Item eventKey={`${e.entryKind}:${e.entryId}`} key={`${e.entryKind}:${e.entryId}`}>
                            <Accordion.Header>{renderRowSummary(e)}</Accordion.Header>
                            <Accordion.Body>{renderEntryBody(e)}</Accordion.Body>
                          </Accordion.Item>
                        ))}
                      </Accordion>
                    )}
                  </>
                )}
              </>
            ) : null}
          </Col>
        </Row>
          </>
        ) : null}
      </Container>
    </div>
  );
};

export default FacilitatorStudentPortfolios;
